import { PixelUnit } from '@starrez/starryui/dist/Theme';
import * as React from 'react';
import styled from 'styled-components';
import { IAvailableFilter, IBarFilterProps, ICurrentFilter, IData, IFilterBarProps } from './';
import { AddFilterButton } from './components/AddFilterButton';
import { SelectFilterDropdown } from './components/SelectFilterDropdown';
import { FilterBarContext } from './FilterBar.context';

const initialValue = (allAvailableFilters: IAvailableFilter[], value: ICurrentFilter[]): IAvailableFilter[] => {
    const activeFilters: IAvailableFilter[] = [];

    if (value) {
        value.forEach((saved) => {
            const item = allAvailableFilters.find((filterItem) => filterItem.name === saved.name);
            if (item) {
                item.value = saved.value;
                activeFilters.push(item);
            }
        });
        return activeFilters;
    }
    return [];
};

export const FilterBar = React.forwardRef(({ children = [], onFilterTypeAdd, onChange, value = [], disabled, showSearch }: IFilterBarProps, ref) => {
    const allAvailableFilters: IAvailableFilter[] = React.Children.map(children, (child: React.ReactElement<IBarFilterProps>) => ({
        multiple: child.props.type === 'MultipleSelect',
        name: child.props.name,
    }));

    const [activeFilters, setActiveFilters] = React.useState<IAvailableFilter[]>(initialValue(allAvailableFilters, value));

    React.useImperativeHandle(ref, () => ({
        clearFilters() {
            setActiveFilters([]);
        },
        setFilters(data: IAvailableFilter[]) {
            setActiveFilters(data);
        },
    }));

    const handleAddNewFilter = (filter: IAvailableFilter) => {
        const item = allAvailableFilters.find((filterItem) => filterItem.name === filter.name);
        if (item) {
            item.showOptions = true;
            setActiveFilters([...activeFilters, item]);
        }
    };

    const handleSelect = (filter: IAvailableFilter, value: IData[]) => {
        if (value && value.length) {
            // we have some values
            const item = activeFilters.find((filterItem) => filterItem.name === filter.name);
            if (item) {
                item.showOptions = false;
                item.value = value;
            }
            setActiveFilters([...activeFilters]);

            fireOnChangeEvent([...activeFilters]);
        } else {
            // we dont have any values so remove this item
            const filters = activeFilters.filter((e) => e.name !== filter.name);
            setActiveFilters([...filters]);

            fireOnChangeEvent([...filters]);
        }
    };

    const handleRemove = (filter: IAvailableFilter) => {
        const itemIndex = activeFilters.findIndex((filterItem) => filterItem.name === filter.name);
        activeFilters.splice(itemIndex, 1);
        setActiveFilters([...activeFilters]);
        fireOnChangeEvent([...activeFilters]);
    };

    const fireOnChangeEvent = (filters: ICurrentFilter[]) => {
        const currentValues: ICurrentFilter[] = filters.filter((e) => e.value);
        onChange(currentValues);
    };

    return (
        <FilterBarContext.Provider
            value={{
                filterTypes: allAvailableFilters,
                hasFilters: activeFilters.filter((filter) => filter.value).length > 0,

                doesItemHaveValue: (name: string) => {
                    return activeFilters.find((filter) => filter.name === name) !== undefined;
                },

                onFilterTypeAdd,
            }}
        >
            <FilterBarWrapperDiv>
                {activeFilters &&
                    activeFilters.map((filter) => (
                        <SelectFilterDropdown key={filter.name} onRemove={handleRemove} onSelected={handleSelect} filter={filter} />
                    ))}

                <div>
                    <AddFilterButton onFilterTypeClick={handleAddNewFilter} disabled={disabled} showSearch={showSearch} />
                </div>
            </FilterBarWrapperDiv>
        </FilterBarContext.Provider>
    );
});

// to overwrite the css from srw Content/_reset.css
export const WrapperDiv = styled.div`
    span {
        cursor: inherit;
    }
`;

export const FilterBarWrapperDiv = styled(WrapperDiv)`
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    > div {
        margin: 0 ${PixelUnit(1)} ${PixelUnit(1)} 0;
    }
`;
